import {
  IconBrain,
  IconHeartbeat,
  IconPill,
  IconRibbonHealth,
  IconShieldCheck,
  IconStethoscope,
  IconVirus,
  type Icon,
} from '@tabler/icons-react'

/**
 * Visual identity of each clinical-family group on the dashboard: an accent colour and an icon.
 * Keyed by the registry's group slugs (seeded in migration 0004); a group added later falls
 * back to a neutral theme rather than going unstyled. Class names are literal so Tailwind keeps
 * them.
 */
export type FamilyTheme = {
  icon: Icon
  /** Top border of the family card. */
  border: string
  /** Icon tile: tinted background and accent icon colour. */
  tile: string
}

const FAMILY_THEMES: Record<string, FamilyTheme> = {
  cancer: { icon: IconRibbonHealth, border: 'border-t-blue-500', tile: 'bg-blue-50 text-blue-600' },
  'chronic-disease': {
    icon: IconHeartbeat,
    border: 'border-t-red-500',
    tile: 'bg-red-50 text-red-600',
  },
  'mental-health-substance': {
    icon: IconBrain,
    border: 'border-t-amber-500',
    tile: 'bg-amber-50 text-amber-600',
  },
  'infectious-disease': {
    icon: IconVirus,
    border: 'border-t-emerald-500',
    tile: 'bg-emerald-50 text-emerald-600',
  },
  'prevention-wellness': {
    icon: IconShieldCheck,
    border: 'border-t-violet-500',
    tile: 'bg-violet-50 text-violet-600',
  },
  'medication-safety': {
    icon: IconPill,
    border: 'border-t-cyan-500',
    tile: 'bg-cyan-50 text-cyan-600',
  },
}

const FALLBACK: FamilyTheme = {
  icon: IconStethoscope,
  border: 'border-t-slate-400',
  tile: 'bg-slate-100 text-slate-600',
}

export const familyTheme = (groupSlug: string): FamilyTheme => FAMILY_THEMES[groupSlug] ?? FALLBACK

/**
 * How urgent a measure looks on Focus Now, from the share of its open patients graded high:
 * the card's left accent and its patients pill.
 */
export function measureUrgency(highPatients: number, openPatients: number) {
  const share = openPatients > 0 ? highPatients / openPatients : 0
  if (share >= 0.5) return { accent: 'border-l-red-500', pill: 'bg-red-50 text-red-700' }
  if (share >= 0.2) return { accent: 'border-l-amber-500', pill: 'bg-amber-50 text-amber-700' }
  return { accent: 'border-l-blue-500', pill: 'bg-blue-50 text-blue-700' }
}
